import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Paper,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material';
import { useStore } from 'zustand';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { useUnityBuildStore } from '../../stores/store';
import { useSetSelectedAssemblyFromAssemblyPicker } from '../../hooks/useSetSelectedAssembly';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { keepBoxInView } from '../../utils/keepBoxInView';
import { EstimatePoint } from '../../api/protosCompiled/estimateAssembly/estimateAssembly_pb';
import { ChangeOrderPoint } from '../../api/protosCompiled/changeOrderAssembly/changeOrderAssembly_pb';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { useHandleGlobalStateState } from '../../hooks/useHandleGlobalState';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Draggable, { DraggableEventHandler, DraggableData } from 'react-draggable';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';
import { v4 as uuidv4 } from 'uuid';

/**
 * Combined type for convenience:
 * Represents a single point in either Estimate or ChangeOrder assemblies.
 */
export type Point = EstimatePoint.AsObject | ChangeOrderPoint.AsObject;

interface PointEditorProps {
  initialPosition?: { x: number; y: number };
  disableAutoPositioning?: boolean;
}

export const PointEditor = ({
  initialPosition,
  disableAutoPositioning = false,
}: PointEditorProps) => {
  const store = useStore(useUnityBuildStore);
  const { selectedPoints, setHasCopiedItems, setSelectedPoints } = useStore(useSelectionStore);

  const cursorPosition = useStore(useUnityBuildStore).cursorPosition;
  const assemblyIndex = useStore(useUnityBuildStore).assemblyIndex;
  const pointIndex = useStore(useUnityBuildStore).pointIndex;
  const includeAllPoints = useStore(useUnityBuildStore).includeAllPoints;
  const userScale = useStore(useUnityBuildStore).userScale;
  const setIsEditingPoint = useStore(useUnityBuildStore).setIsEditingPoint;
  const setIsAnnotatingTakeoff = useStore(useUnityBuildStore).setIsAnnotatingTakeoff;
  const setIncludeAllPoints = useStore(useUnityBuildStore).setIncludeAllPoints;
  const setIsInsertingPoint = useStore(useUnityBuildStore).setIsInsertingPoint;
  const setIsViewingColorEditor = useStore(useUnityBuildStore).setIsViewingColorEditor;
  const setHasPointEditorInteraction = useStore(useUnityBuildStore).setHasPointEditorInteraction;
  const setHasActiveRunStarted = useStore(useUnityBuildStore).setHasActiveRunStarted;

  const { currentTakeoffAssembly, setCurrentTakeoffAssembly, setIsViewingTakeoffEditor } =
    useStore(useAssemblyStore);

  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();

  const { submitAssembly } = useSubmitTakeoffAssembly();
  const setSelectedAssembly = useSetSelectedAssemblyFromAssemblyPicker();

  const [verticalLength, setVerticalLength] = useState<number>(0);

  // Local checkbox states
  const [localIsIncludedInCount, setLocalIsIncludedInCount] = useState(false);
  const [localAllowTrailingLine, setLocalAllowTrailingLine] = useState(false);
  // Add local state to track if we've performed a "select all" operation
  const [hasSelectedAll, setHasSelectedAll] = useState(false);

  const menuOptions = {
    addToRun: 'Add to Run',
    completeRun: 'Complete Run',
    insertPoint: 'Insert Point',
    insertPointActive: '🔄 Insert Mode Active',
    includeAllPoints: 'Include All Points',
    allowTrailingLine: 'Allow Trailing Line',
    copyAsNew: 'Copy As New',
    deleteSelection: 'Delete Selection',
    addVertical: 'Add Vertical',
    deletePoint: 'Delete Point',
    toggleIncludeInCount: 'Toggle Include In Count',
    toggleAllowTrailingLine: 'Toggle Allow Trailing Line',
    copyExtend: 'Copy Extend',
    editStyle: 'Edit Style',
  };

  // If we have a valid pointIndex => "point editing"
  const isPointEditing = pointIndex !== null && pointIndex !== undefined;

  // Check if we have selected points to enable copy/paste functionality
  const hasSelectedPoints = selectedPoints && selectedPoints.length > 0;

  // New condition: Either has selected points OR using include all points with a valid assembly
  const canCopyPoints =
    hasSelectedPoints ||
    (includeAllPoints &&
      currentTakeoffAssembly &&
      currentTakeoffAssembly.pointsList &&
      currentTakeoffAssembly.pointsList.length > 0);

  // Box dimensions
  const boxWidth = 250;
  // Remove fixed height - let it grow to fit content

  const draggableRef = useRef<HTMLDivElement>(null);
  // If auto-positioning is disabled, use a position of 0,0 as we're already positioned by the parent
  const [position, setPosition] = useState<{ x: number; y: number }>(
    disableAutoPositioning ? { x: 0, y: 0 } : initialPosition || { x: 0, y: 0 }
  );
  const [hasDragged, setHasDragged] = useState(false);

  // Store previous isEditingPoint value to detect when the editor is opened
  const previousIsEditingPointRef = useRef(false);

  // Position the editor when it first opens - only if auto positioning is not disabled
  useEffect(() => {
    // Skip this effect if auto positioning is disabled
    if (disableAutoPositioning) return;

    const isEditingPoint = store.isEditingPoint;

    // Detect when the editor is first opened
    if (isEditingPoint && !previousIsEditingPointRef.current) {
      // Reset the dragged state when editor is first opened
      setHasDragged(false);

      if (cursorPosition) {
        // Position near cursor with a small offset
        const offsetX = 20;
        const offsetY = 20;

        // Check if TakeoffPallette is visible to avoid overlap
        const isTakeoffPaletteVisible = useKeyBoardEventStore.getState().isViewingTakeoffPallette;
        const isColorEditorVisible = store.isViewingColorEditor;

        // Add additional offset if other components are visible to avoid overlap
        let additionalOffsetX = 0;

        if (isTakeoffPaletteVisible) {
          // Add offset to avoid overlapping with TakeoffPallette
          additionalOffsetX += 50;

          // Add more offset if ColorAndStyleEditor is also visible
          if (isColorEditorVisible) {
            additionalOffsetX += 500;
          }
        }

        const { x, y } = keepBoxInView(
          cursorPosition.x + offsetX - additionalOffsetX,
          cursorPosition.y + offsetY,
          boxWidth,
          400, // Use a reasonable default height for positioning
          window.innerWidth,
          window.innerHeight
        );

        setPosition({ x, y });
      } else {
        // If no cursor position, center in viewport
        const x = Math.max(0, (window.innerWidth - boxWidth) / 2);
        const y = Math.max(0, (window.innerHeight - 400) / 2); // Use reasonable default height
        setPosition({ x, y });
      }
    }

    // Update the ref
    previousIsEditingPointRef.current = isEditingPoint;
  }, [
    store.isViewingColorEditor,
    store.isEditingPoint,
    cursorPosition,
    boxWidth,
    disableAutoPositioning,
  ]);

  // Handle the start of dragging
  const handleDragStart: DraggableEventHandler = () => {
    setHasDragged(true);
  };

  // Handle dragging - now with no constraints during drag
  const handleDrag: DraggableEventHandler = (e, data: DraggableData) => {
    // Only update position if user has started dragging
    if (hasDragged) {
      // Update position directly with coordinates from drag event
      setPosition({
        x: data.x,
        y: data.y,
      });
    }
  };

  /**
   * On mount/update:
   *   Read the point's verticalLength, isIncludedInCount, and allowTrailingLine
   */
  useEffect(() => {
    if (currentTakeoffAssembly && currentTakeoffAssembly.pointsList && isPointEditing) {
      const pt = currentTakeoffAssembly.pointsList[pointIndex];
      setVerticalLength(pt?.verticallength ?? 0);
      setLocalIsIncludedInCount(pt?.isincludedincount ?? false);
      setLocalAllowTrailingLine(pt?.allowtrailingline ?? false);
    }
  }, [currentTakeoffAssembly, pointIndex, isPointEditing]);

  // --------------------------
  //    Existing Functionality
  // --------------------------
  const handleAddToRun = (assembly: TakeoffAssembly) => {
    // Smart "Add to Run" functionality with type-specific handling:
    // - LENGTH: Create new point at selected location (beautiful for linear takeoffs)
    // - AREA: Reorder points for consistent polygon rendering
    // - COUNT: Use standard behavior (no special handling)
    // - If selected from TakeoffPalletteTab: continue from the end of the existing run

    if (isPointEditing && pointIndex !== null && pointIndex !== undefined) {
      const selectedPoint = assembly.pointsList[pointIndex];
      let modifiedAssembly;

      if (assembly.assemblymeasurementtype === 'length') {
        // LENGTH ASSEMBLIES: Create new point at selected location (beautiful for linear takeoffs)
        const newStartingPoint = {
          pointid: uuidv4(), // Generate unique ID
          x: selectedPoint.x,
          y: selectedPoint.y,
          angle: selectedPoint.angle || 0,
          verticallength: 0,
          allowtrailingline: false, // Set to false for clean takeoff
          allowtrailingmeasurement: true,
          isincludedincount: false, // Set to false to avoid double counting
          isselected: false,
          isactive: true,
        };

        modifiedAssembly = {
          ...assembly,
          pointsList: [...assembly.pointsList, newStartingPoint],
        };
      } else if (assembly.assemblymeasurementtype === 'area') {
        // AREA ASSEMBLIES: Reorder points for consistent polygon rendering
        const originalPoints = [...assembly.pointsList];

        // Reorder points so the clicked point becomes the LAST point in the sequence
        // This way, when we continue drawing, new points will be added after the clicked point
        const reorderedPoints = [
          ...originalPoints.slice(pointIndex + 1),
          ...originalPoints.slice(0, pointIndex + 1),
        ];

        // Intelligently set allowtrailingline for area assemblies
        const smartPoints = reorderedPoints.map((point, index) => ({
          ...point,
          // The clicked point (now last in the reordered array) should not have trailing line
          allowtrailingline: index === reorderedPoints.length - 1 ? false : true,
        }));

        modifiedAssembly = {
          ...assembly,
          pointsList: smartPoints,
        };
      } else {
        // COUNT ASSEMBLIES: Do nothing special, use standard behavior
        modifiedAssembly = assembly;
      }

      setIsEditingPoint({
        isEditingPoint: false,
        isEditingStyle: false,
        isViewingTakeoffEditor: false,
      });
      setIsAnnotatingTakeoff(true);
      setSelectedAssembly({
        takeoffAssembly: modifiedAssembly,
      });
    } else {
      // Default behavior when not editing a specific point (selected from TakeoffPalletteTab)
      // Continue from the end of the existing run with original point order
      setIsEditingPoint({
        isEditingPoint: false,
        isEditingStyle: false,
        isViewingTakeoffEditor: false,
      });
      setIsAnnotatingTakeoff(true);
      setSelectedAssembly({
        takeoffAssembly: assembly,
      });
    }
  };
  const handleGlobalState = useHandleGlobalStateState();

  const handleCompleteRun = () => {
    if (currentTakeoffAssembly) {
      try {
        if (currentTakeoffAssembly.assemblyid) {
          void submitAssembly({
            shouldUpdateBreakout: false,
            assembly: currentTakeoffAssembly,
            isUpdating: true,
            pageScale: userScale,
          });
        } else {
          void submitAssembly({
            shouldUpdateBreakout: false,
            assembly: currentTakeoffAssembly,
            isCreating: true,
            pageScale: userScale,
          });
        }
      } catch (error) {
        console.error('Error submitting assembly:', error);
      }
    }
    handleGlobalState({
      resetOption: 'resetTakeoff',
    });
    setIsAnnotatingTakeoff(false);
    // Clear the active run flag when run is complete
    useUnityBuildStore.getState().setHasActiveRun(false);
    setIsEditingPoint({
      isEditingPoint: false,
      isEditingStyle: false,
      isViewingTakeoffEditor: false,
    });
  };

  // Add a new function to select all points in the current assembly
  const handleSelectAllPoints = useCallback(() => {
    if (!currentTakeoffAssembly || !currentTakeoffAssembly.pointsList) {
      console.log('[SELECT ALL POINTS] No assembly or points available to select');
      return;
    }

    // Clear any existing paste preview when selection changes
    document.dispatchEvent(new CustomEvent('app:clearpastepreview'));

    // Clear global paste preview states
    if ((window as any).__pastePreviewActive) {
      (window as any).__pastePreviewActive = false;
    }
    if ((window as any).__pastePositionFrozen) {
      (window as any).__pastePositionFrozen = false;
    }
    if ((window as any).__frozenPastePosition) {
      (window as any).__frozenPastePosition = null;
    }
    if ((window as any).__currentPastePreviewOffset) {
      (window as any).__currentPastePreviewOffset = null;
    }

    // Create selection objects for all points in the current assembly
    const allPoints = currentTakeoffAssembly.pointsList.map(point => ({
      pointId: point.pointid,
      assemblyId: currentTakeoffAssembly.assemblyid,
      originalX: point.x,
      originalY: point.y,
    }));

    // Update the selection store
    setSelectedPoints(allPoints);
    setHasSelectedAll(true);

    // Set the hasCopiedItems flag to enable copy operations
    setHasCopiedItems(true);

    // Immediately trigger a paste preview to show the selection
    // This gives users immediate visual feedback that points are selected
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent('app:pastepreview', {
          detail: { source: 'select-all-points' },
        })
      );
    }, 100); // Increased delay to ensure selection is properly set
  }, [currentTakeoffAssembly, setSelectedPoints, setHasCopiedItems]);

  // Modified handleCopyExtend to add more debug logging
  const handleCopyExtend = () => {
    // If using "Include all points", first select all points in the assembly
    if (includeAllPoints && currentTakeoffAssembly && currentTakeoffAssembly.pointsList) {
      // Select all points first and ensure the selection is complete
      handleSelectAllPoints();

      // Force a synchronous update to the selection store
      const allPoints = currentTakeoffAssembly.pointsList.map(point => ({
        pointId: point.pointid,
        assemblyId: currentTakeoffAssembly.assemblyid,
        originalX: point.x,
        originalY: point.y,
      }));

      // Directly update the selection store instead of through React state
      const selectionStore = useSelectionStore.getState();
      selectionStore.setSelectedPoints(allPoints);

      // Make sure the copied items flag is set
      setHasCopiedItems(true);

      // Set copy mode to 'extend' before triggering copy
      selectionStore.setCopyMode('extend');

      // Then dispatch the copy event
      const event = new CustomEvent('app:copy', {
        detail: {
          forceSelection: allPoints,
          source: 'include-all-points',
        },
      });
      document.dispatchEvent(event);

      // Trigger paste preview after copy with a longer delay to ensure copy completes
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('app:pastepreview'));
      }, 150); // Increased delay to ensure copy operation completes

      // Keep style editor open if it's active
      const isEditingStyle = store.isEditingStyle;
      setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
      return;
    }

    // Normal flow for already selected points
    // Set copy mode to 'extend' before triggering copy
    const selectionStore = useSelectionStore.getState();
    selectionStore.setCopyMode('extend');

    // Then dispatch the copy event
    const event = new CustomEvent('app:copy');
    document.dispatchEvent(event);

    // Manually update the hasCopiedItems state to ensure UI updates
    setHasCopiedItems(true);

    // Trigger paste preview after copy with a shorter delay
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('app:pastepreview'));
    }, 50);

    // Keep style editor open if it's active
    const isEditingStyle = store.isEditingStyle;
    setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
  };

  // Modified handleCopyAsNew to work with includeAllPoints
  const handleCopyAsNew = () => {
    // If using "Include all points", first select all points in the assembly
    if (includeAllPoints && currentTakeoffAssembly && currentTakeoffAssembly.pointsList) {
      // Select all points first and ensure the selection is complete
      handleSelectAllPoints();

      // Force a synchronous update to the selection store
      const allPoints = currentTakeoffAssembly.pointsList.map(point => ({
        pointId: point.pointid,
        assemblyId: currentTakeoffAssembly.assemblyid,
        originalX: point.x,
        originalY: point.y,
      }));

      // Directly update the selection store instead of through React state
      const selectionStore = useSelectionStore.getState();
      selectionStore.setSelectedPoints(allPoints);

      // Set copy mode to 'create' before triggering copy
      selectionStore.setCopyMode('create');

      // Then dispatch the copy event
      const event = new CustomEvent('app:copy');
      document.dispatchEvent(event);

      // Manually update the hasCopiedItems state to ensure UI updates
      setHasCopiedItems(true);

      // Trigger paste preview after copy with a longer delay to ensure copy completes
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('app:pastepreview'));
      }, 150); // Increased delay to ensure copy operation completes

      // Keep style editor open if it's active
      const isEditingStyle = store.isEditingStyle;
      setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
      return;
    }

    // Normal flow for already selected points
    // Set copy mode to 'create' before triggering copy
    const selectionStore = useSelectionStore.getState();
    selectionStore.setCopyMode('create');

    // Then dispatch the copy event
    const event = new CustomEvent('app:copy');
    document.dispatchEvent(event);

    // Manually update the hasCopiedItems state to ensure UI updates
    setHasCopiedItems(true);

    // Trigger paste preview after copy with a shorter delay
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('app:pastepreview'));
    }, 50);

    // Keep style editor open if it's active
    const isEditingStyle = store.isEditingStyle;
    setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
  };

  const handleDeletePoint = (assemblyIdx: number, pIndex: number) => {
    if (!currentTakeoffAssembly) return;

    let updatedPoints: Point[] = [...currentTakeoffAssembly.pointsList];

    // If "Include All Points" is checked => remove ALL points
    if (includeAllPoints) {
      updatedPoints = [];
    } else {
      updatedPoints = updatedPoints.filter((_, idx) => idx !== pIndex);
    }

    const updatedAssembly = {
      ...currentTakeoffAssembly,
      pointsList: updatedPoints,
    };

    // Fire-and-forget save for optimistic UX
    void submitAssembly({
      shouldUpdateBreakout: false,
      assembly: updatedAssembly,
      isUpdating: true,
      pageScale: userScale,
    });

    // Immediately reset UI state
    handleGlobalState({
      resetOption: 'resetTakeoff',
    });
  };

  const handleAddVertical = (pIndex: number) => {
    if (includeAllPoints && currentTakeoffAssembly) {
      // Apply to all points in current assembly
      store.setIsAddingVertical(true);
      let updatedPoints = [...currentTakeoffAssembly.pointsList];

      // Apply verticalLength to all points
      updatedPoints = updatedPoints.map(pt => ({
        ...pt,
        verticallength: verticalLength,
      }));

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });

      // Immediately clear UI state
      setSelectedPoints([]);
      setHasSelectedAll(false);
    } else if (hasSelectedPoints && selectedPoints.length > 0) {
      // Apply to multiple selected points, potentially across multiple assemblies
      store.setIsAddingVertical(true);

      // Group selected points by assembly ID
      const pointsByAssembly = new Map();

      // First, fetch all assemblies that might contain our selected points (current page only)
      const assemblies = Array.isArray(filteredCountedAssemblies)
        ? []
        : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
            (group: any) => group.assembliesList || []
          ) || [];

      // Filter only length-type assemblies
      const lengthAssemblies = assemblies.filter(
        (assembly: any) => assembly.assemblymeasurementtype === 'length'
      );

      // Create a mapping of assemblyId -> assembly for faster lookup
      const assemblyMap = new Map(
        lengthAssemblies.map((assembly: any) => [assembly.assemblyid, assembly])
      );

      // Group selected points by their assembly
      for (const selectedPoint of selectedPoints) {
        if (!pointsByAssembly.has(selectedPoint.assemblyId)) {
          const assembly = assemblyMap.get(selectedPoint.assemblyId);

          // Skip if assembly doesn't exist or isn't a length type
          if (!assembly) continue;

          pointsByAssembly.set(selectedPoint.assemblyId, {
            assembly,
            pointIds: new Set(),
          });
        }

        // Add point ID to the set for this assembly
        pointsByAssembly.get(selectedPoint.assemblyId)?.pointIds.add(selectedPoint.pointId);
      }

      // Fire-and-forget updates for optimistic UX
      for (const [, { assembly, pointIds }] of pointsByAssembly.entries()) {
        if (!assembly.pointsList) continue;

        // Apply verticalLength to all selected points in this assembly
        const updatedPoints = assembly.pointsList.map((point: Point) => {
          if (pointIds.has(point.pointid)) {
            return {
              ...point,
              verticallength: verticalLength,
            };
          }
          return point;
        });

        const updatedAssembly = {
          ...assembly,
          pointsList: updatedPoints,
        };

        // Fire-and-forget save
        void submitAssembly({
          shouldUpdateBreakout: false,
          assembly: updatedAssembly,
          isUpdating: true,
          pageScale: userScale,
        });
      }

      // Immediately clear UI state
      setSelectedPoints([]);
      setHasSelectedAll(false);
    } else if (isPointEditing && currentTakeoffAssembly) {
      // Apply to a single point in edit mode
      store.setIsAddingVertical(true);
      let updatedPoints = [...currentTakeoffAssembly.pointsList];

      // Apply verticalLength only to the single selected point
      updatedPoints[pIndex] = {
        ...updatedPoints[pIndex],
        verticallength: verticalLength,
      };

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isPointEditing) {
        handleAddVertical(pointIndex!);
      } else if (hasSelectedPoints) {
        handleAddVertical(-1); // Pass any value, it will be ignored when hasSelectedPoints is true
      }
    }
  };

  const handleVerticalLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setVerticalLength(isNaN(value) ? 0 : Math.max(0, value));
  };

  const handleIncludeAllPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    setIncludeAllPoints(isChecked);

    // Clear any existing paste preview when selection changes
    document.dispatchEvent(new CustomEvent('app:clearpastepreview'));

    // Clear global paste preview states
    if ((window as any).__pastePreviewActive) {
      (window as any).__pastePreviewActive = false;
    }
    if ((window as any).__pastePositionFrozen) {
      (window as any).__pastePositionFrozen = false;
    }
    if ((window as any).__frozenPastePosition) {
      (window as any).__frozenPastePosition = null;
    }
    if ((window as any).__currentPastePreviewOffset) {
      (window as any).__currentPastePreviewOffset = null;
    }

    // If turning on "Include all points", auto-select all points and show preview
    if (isChecked && currentTakeoffAssembly && currentTakeoffAssembly.pointsList) {
      handleSelectAllPoints();
    } else if (!isChecked) {
      // Clear selection when unchecking "Include all points"
      setSelectedPoints([]);
      setHasSelectedAll(false);
    }
  };

  const handleSetInsertPoint = () => {
    setHasPointEditorInteraction(true);
    setIsInsertingPoint(true);
    // Keep style editor open if it's active
    const isEditingStyle = store.isEditingStyle;
    setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
  };

  const handleEditStyles = useCallback(
    (assembly: TakeoffAssembly) => {
      console.log('open the Color & Style Editor', assembly);
      setHasPointEditorInteraction(true);
      setIsViewingColorEditor(true);
      setIsViewingTakeoffEditor(false);
    },
    [setIsViewingColorEditor, setIsViewingTakeoffEditor, setHasPointEditorInteraction]
  );

  // Modified handleDeleteSelection to clear selection after deletion
  const handleDeleteSelection = () => {
    // Dispatch a custom event to trigger deletion
    const event = new CustomEvent('app:delete');
    document.dispatchEvent(event);

    // Immediately clear selected points for optimistic UX
    setSelectedPoints([]);
    setHasSelectedAll(false);

    // Keep style editor open if it's active
    const isEditingStyle = store.isEditingStyle;
    setIsEditingPoint({ isEditingPoint: false, isEditingStyle, isViewingTakeoffEditor: false });
  };

  // -------------
  //  NEW LOGIC
  // -------------
  /** handleToggleIncludeInCount:
   *   Changes 'isincludedincount' for:
   *   - All points if "includeAllPoints" = true
   *   - All selected points if hasSelectedPoints = true
   *   - The single point (pointIndex) if editing single point
   */
  const handleToggleIncludeInCount = (checked: boolean) => {
    if (!currentTakeoffAssembly) return;

    if (includeAllPoints) {
      // Apply to all points in current assembly
      let updatedPoints = [...currentTakeoffAssembly.pointsList];
      updatedPoints = updatedPoints.map(pt => ({
        ...pt,
        isincludedincount: checked,
      }));

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });

      // Immediately clear UI state
      setSelectedPoints([]);
      setHasSelectedAll(false);
    } else if (hasSelectedPoints && selectedPoints.length > 0) {
      // Apply to multiple selected points, potentially across multiple assemblies
      const pointsByAssembly = new Map();

      // Get all assemblies that might contain our selected points
      const assemblies = Array.isArray(filteredCountedAssemblies)
        ? []
        : filteredCountedAssemblies?.filteredAssemblies?.[0]?.assembliesList || [];

      // Create a mapping of assemblyId -> assembly for faster lookup
      const assemblyMap = new Map(
        assemblies.map((assembly: any) => [assembly.assemblyid, assembly])
      );

      // Group selected points by their assembly
      for (const selectedPoint of selectedPoints) {
        if (!pointsByAssembly.has(selectedPoint.assemblyId)) {
          const assembly = assemblyMap.get(selectedPoint.assemblyId);
          if (!assembly) continue;

          pointsByAssembly.set(selectedPoint.assemblyId, {
            assembly,
            pointIds: new Set(),
          });
        }
        pointsByAssembly.get(selectedPoint.assemblyId)?.pointIds.add(selectedPoint.pointId);
      }

      // Fire-and-forget updates for optimistic UX
      for (const [, { assembly, pointIds }] of pointsByAssembly.entries()) {
        if (!assembly.pointsList) continue;

        const updatedPoints = assembly.pointsList.map((point: Point) => {
          if (pointIds.has(point.pointid)) {
            return {
              ...point,
              isincludedincount: checked,
            };
          }
          return point;
        });

        const updatedAssembly = {
          ...assembly,
          pointsList: updatedPoints,
        };

        // Fire-and-forget save
        void submitAssembly({
          shouldUpdateBreakout: false,
          assembly: updatedAssembly,
          isUpdating: true,
          pageScale: userScale,
        });
      }
    } else if (isPointEditing) {
      // Apply to single point in edit mode
      let updatedPoints = [...currentTakeoffAssembly.pointsList];
      const orig = updatedPoints[pointIndex];
      updatedPoints[pointIndex] = {
        ...orig,
        isincludedincount: checked,
      };

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });
    }

    // Update local state so checkbox stays in sync
    setLocalIsIncludedInCount(checked);
  };

  /** handleToggleAllowTrailingLine:
   *   Changes 'allowtrailingline' for:
   *   - All points if "includeAllPoints" = true
   *   - All selected points if hasSelectedPoints = true
   *   - The single point (pointIndex) if editing single point
   */
  const handleToggleAllowTrailingLine = (checked: boolean) => {
    if (!currentTakeoffAssembly) return;

    if (includeAllPoints) {
      // Apply to all points in current assembly
      let updatedPoints = [...currentTakeoffAssembly.pointsList];
      updatedPoints = updatedPoints.map(pt => ({
        ...pt,
        allowtrailingline: checked,
      }));

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });

      // Immediately clear UI state
      setSelectedPoints([]);
      setHasSelectedAll(false);
    } else if (hasSelectedPoints && selectedPoints.length > 0) {
      // Apply to multiple selected points, potentially across multiple assemblies
      const pointsByAssembly = new Map();

      // Get all assemblies that might contain our selected points
      const assemblies = Array.isArray(filteredCountedAssemblies)
        ? []
        : filteredCountedAssemblies?.filteredAssemblies?.[0]?.assembliesList || [];

      // Create a mapping of assemblyId -> assembly for faster lookup
      const assemblyMap = new Map(
        assemblies.map((assembly: any) => [assembly.assemblyid, assembly])
      );

      // Group selected points by their assembly
      for (const selectedPoint of selectedPoints) {
        if (!pointsByAssembly.has(selectedPoint.assemblyId)) {
          const assembly = assemblyMap.get(selectedPoint.assemblyId);
          if (!assembly) continue;

          pointsByAssembly.set(selectedPoint.assemblyId, {
            assembly,
            pointIds: new Set(),
          });
        }
        pointsByAssembly.get(selectedPoint.assemblyId)?.pointIds.add(selectedPoint.pointId);
      }

      // Fire-and-forget updates for optimistic UX
      for (const [, { assembly, pointIds }] of pointsByAssembly.entries()) {
        if (!assembly.pointsList) continue;

        const updatedPoints = assembly.pointsList.map((point: Point) => {
          if (pointIds.has(point.pointid)) {
            return {
              ...point,
              allowtrailingline: checked,
            };
          }
          return point;
        });

        const updatedAssembly = {
          ...assembly,
          pointsList: updatedPoints,
        };

        // Fire-and-forget save
        void submitAssembly({
          shouldUpdateBreakout: false,
          assembly: updatedAssembly,
          isUpdating: true,
          pageScale: userScale,
        });
      }
    } else if (isPointEditing) {
      // Apply to single point in edit mode
      let updatedPoints = [...currentTakeoffAssembly.pointsList];
      const orig = updatedPoints[pointIndex];
      updatedPoints[pointIndex] = {
        ...orig,
        allowtrailingline: checked,
      };

      const updatedAssembly = {
        ...currentTakeoffAssembly,
        pointsList: updatedPoints,
      };

      // Fire-and-forget save for optimistic UX
      void submitAssembly({
        shouldUpdateBreakout: false,
        assembly: updatedAssembly,
        isUpdating: true,
        pageScale: userScale,
      });
    }

    // Update local state so checkbox stays in sync
    setLocalAllowTrailingLine(checked);
  };

  // Add effect to ensure the copy event has access to the selected points
  useEffect(() => {
    const handleAppCopy = (event: Event) => {
      // Get any optional details passed with the event
      const customEvent = event as CustomEvent;
      const details = customEvent.detail || {};

      // Clear any existing paste preview when new copy operation starts
      document.dispatchEvent(new CustomEvent('app:clearpastepreview'));

      // Clear global paste preview states
      if ((window as any).__pastePreviewActive) {
        (window as any).__pastePreviewActive = false;
      }
      if ((window as any).__pastePositionFrozen) {
        (window as any).__pastePositionFrozen = false;
      }

      // Check if we have force selection data from the event
      const forceSelection = details?.forceSelection;

      // If we have "Include all points" checked, make sure all points are selected
      if (forceSelection) {
        // Use the forced selection directly
        const selectionStore = useSelectionStore.getState();
        selectionStore.setSelectedPoints(forceSelection);
      } else if (includeAllPoints && currentTakeoffAssembly && currentTakeoffAssembly.pointsList) {
        if (!selectedPoints || selectedPoints.length < currentTakeoffAssembly.pointsList.length) {
          // Force synchronous selection of all points
          const allPoints = currentTakeoffAssembly.pointsList.map(point => ({
            pointId: point.pointid,
            assemblyId: currentTakeoffAssembly.assemblyid,
            originalX: point.x,
            originalY: point.y,
          }));

          // Directly update the selection store
          const selectionStore = useSelectionStore.getState();
          selectionStore.setSelectedPoints(allPoints);
        }
      }

      // Log what's actually being copied after all our logic
      const finalSelection = useSelectionStore.getState().selectedPoints;
      if (finalSelection && finalSelection.length > 0) {
      }

      // Update hasCopiedItems state since we know a copy just happened
      setHasCopiedItems(true);
    };

    document.addEventListener('app:copy', handleAppCopy);

    return () => {
      document.removeEventListener('app:copy', handleAppCopy);
    };
  }, [
    includeAllPoints,
    currentTakeoffAssembly,
    selectedPoints,
    setHasCopiedItems,
    setSelectedPoints,
  ]);

  // Add effect for keyboard event handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip keyboard events if viewing extension
      if (useUnityBuildStore.getState().isViewingExtension) {
        return;
      }

      // Clear paste preview on Escape key
      if (e.key === 'Escape') {
        document.dispatchEvent(new CustomEvent('app:escapepress'));

        // Clear all selections and states when Escape is pressed
        // Exit insertion mode if active
        if (store.isInsertingPoint) {
          setIsInsertingPoint(false);
        }

        // Clear current assembly selection
        setCurrentTakeoffAssembly(null);

        // Clear all selections using the store's clear functions
        const selectionStore = useSelectionStore.getState();
        selectionStore.clearSelection();
        selectionStore.setCopyMode('create'); // Reset to default copy mode

        // Clear paste preview states
        document.dispatchEvent(new CustomEvent('app:clearpastepreview'));

        // Clear global paste preview states
        if ((window as any).__pastePreviewActive) {
          (window as any).__pastePreviewActive = false;
        }
        if ((window as any).__pastePositionFrozen) {
          (window as any).__pastePositionFrozen = false;
        }

        // Reset takeoff annotation mode
        setIsAnnotatingTakeoff(false);

        // Reset include all points state
        setIncludeAllPoints(false);

        // CRITICAL: Reset all run flags to ensure "Add to Run" state
        const unityStore = useUnityBuildStore.getState();
        unityStore.setHasActiveRun(false);
        unityStore.setHasActiveRunStarted(false);
        unityStore.setHasPointEditorInteraction(false);

        // Close the point editor
        setIsEditingPoint({
          isEditingPoint: false,
          isEditingStyle: false,
          isViewingTakeoffEditor: false,
        });

        // Reset local state
        setHasSelectedAll(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    store.isInsertingPoint,
    setIsInsertingPoint,
    setCurrentTakeoffAssembly,
    setIsAnnotatingTakeoff,
    setIncludeAllPoints,
    setIsEditingPoint,
  ]);

  // --------------------------------
  // Box positioning + rendering
  // --------------------------------

  const theme = useTheme();

  // Check for active paste preview
  const [isPastePreviewFrozen, setIsPastePreviewFrozen] = useState(false);

  // Effect to check for paste preview and update state
  useEffect(() => {
    // Check if we have a frozen paste preview position
    if ((window as any).__pastePositionFrozen) {
      setIsPastePreviewFrozen(true);
      // Clear the flag to avoid it persisting
      (window as any).__pastePositionFrozen = false;
    }
  }, []);

  // Handle saving the pasted items at the current preview position
  const handleSaveAtPosition = () => {
    const selectionStore = useSelectionStore.getState();
    const copiedObjects = selectionStore.copiedObjects;

    if (!copiedObjects || copiedObjects.length === 0) {
      console.error('[SAVE AT POSITION] No copied objects available');
      return;
    }

    // Use the exact same offset that was used to position the paste preview
    // This ensures the saved points are placed exactly where the preview was shown
    const pastePreviewOffset = (window as any).__currentPastePreviewOffset;

    if (!pastePreviewOffset) {
      console.error('[SAVE AT POSITION] No paste preview offset available');
      return;
    }

    // Use the frozen paste position from the global state
    const frozenPosition = (window as any).__frozenPastePosition || cursorPosition;

    // Create an event that triggers the paste action at the frozen position
    const event = new CustomEvent('app:paste:action', {
      detail: {
        source: 'point-editor',
        copyMode: selectionStore.copyMode,
        copiedObjects: copiedObjects,
        pastePosition: frozenPosition,
        offset: pastePreviewOffset, // Use the exact same offset as the preview
      },
    });
    document.dispatchEvent(event);

    // Immediately close editor and clear state for optimistic UX
    setIsEditingPoint({ isEditingPoint: false, isViewingTakeoffEditor: false });
    document.dispatchEvent(new CustomEvent('app:clearpastepreview'));
    selectionStore.clearSelection();
    setIsPastePreviewFrozen(false);
  };

  return (
    <Draggable
      nodeRef={draggableRef}
      position={disableAutoPositioning ? undefined : position}
      onDrag={handleDrag}
      onStart={handleDragStart}
      handle=".drag-handle"
      disabled={false}
    >
      <Paper
        ref={draggableRef}
        onContextMenu={e => e.preventDefault()}
        elevation={4}
        sx={{
          position: 'absolute',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: 2,
          width: boxWidth,
          height: 'auto', // Let it grow to fit content
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          zIndex: 9999,
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'default',
          transition: 'none',
          '& .drag-handle': {
            cursor: 'move',
          },
          '& .MuiFormControlLabel-root': {
            marginRight: 0,
            marginLeft: 0,
            '& .MuiTypography-root': {
              color: theme.palette.text.secondary,
            },
          },
          '& .MuiButton-root': {
            minHeight: 36,
            textTransform: 'none',
            fontWeight: 500,
          },
          '& .MuiTextField-root': {
            marginTop: 1,
            marginBottom: 1,
          },
          '& .MuiTypography-body2': {
            fontSize: '0.875rem',
            lineHeight: 1.2,
          },
          '& .MuiCheckbox-root': {
            color: theme.palette.text.secondary,
            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiDivider-root': {
            borderColor: alpha(theme.palette.divider, 0.1),
          },
        }}
        onClick={e => e.stopPropagation()}
      >
        <Box
          className="drag-handle"
          sx={{
            width: '100%',
            height: '24px',
            position: 'absolute',
            top: 0,
            left: 0,
            cursor: 'move',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        />
        <Box sx={{ mt: 3 }}>
          <Stack spacing={1} width="100%">
            {/* Main buttons */}
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => {
                const hasInteracted = useUnityBuildStore.getState().hasPointEditorInteraction;
                const hasActiveRun = useUnityBuildStore.getState().hasActiveRun;
                const shouldAddToRun =
                  !store.isAnnotatingTakeoff || (!hasInteracted && !hasActiveRun);
                if (shouldAddToRun) {
                  setHasPointEditorInteraction(true);
                  setHasActiveRunStarted(true);
                  handleAddToRun(currentTakeoffAssembly as TakeoffAssembly);
                } else {
                  handleCompleteRun();
                }
              }}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {!store.isAnnotatingTakeoff ||
              (!useUnityBuildStore.getState().hasPointEditorInteraction &&
                !useUnityBuildStore.getState().hasActiveRun)
                ? menuOptions.addToRun
                : menuOptions.completeRun}
            </Button>

            {/* Save paste at position - show only when we have a frozen paste preview */}
            {isPastePreviewFrozen && (
              <Button
                variant="contained"
                size="small"
                fullWidth
                color="success"
                onClick={handleSaveAtPosition}
                sx={{
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  },
                }}
              >
                Save at Current Position
              </Button>
            )}

            {/* Insert Point for length/area assemblies */}
            {(currentTakeoffAssembly?.assemblymeasurementtype === 'length' ||
              currentTakeoffAssembly?.assemblymeasurementtype === 'area') && (
              <>
                <Button
                  variant={store.isInsertingPoint ? 'contained' : 'outlined'}
                  size="small"
                  fullWidth
                  onClick={handleSetInsertPoint}
                  color={store.isInsertingPoint ? 'warning' : 'primary'}
                  sx={{
                    borderColor: store.isInsertingPoint
                      ? theme.palette.warning.main
                      : theme.palette.divider,
                    color: store.isInsertingPoint
                      ? theme.palette.warning.contrastText
                      : theme.palette.text.primary,
                    backgroundColor: store.isInsertingPoint
                      ? theme.palette.warning.main
                      : 'transparent',
                    fontWeight: store.isInsertingPoint ? 'bold' : 'normal',
                    '&:hover': {
                      borderColor: store.isInsertingPoint
                        ? theme.palette.warning.dark
                        : theme.palette.primary.main,
                      backgroundColor: store.isInsertingPoint
                        ? theme.palette.warning.dark
                        : alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  {store.isInsertingPoint ? menuOptions.insertPointActive : menuOptions.insertPoint}
                </Button>

                {/* Help text when in insertion mode */}
                {store.isInsertingPoint && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.warning.main,
                      textAlign: 'center',
                      fontStyle: 'italic',
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    Hover over any line to highlight it, then click to insert a point
                    <br />
                    Press <strong>Escape</strong> to exit insertion mode
                  </Typography>
                )}
              </>
            )}

            <Button
              variant={store.isInsertingPoint ? 'contained' : 'outlined'}
              size="small"
              fullWidth
              onClick={() => handleEditStyles(currentTakeoffAssembly!)}
              color={store.isInsertingPoint ? 'warning' : 'primary'}
              sx={{
                borderColor: store.isInsertingPoint
                  ? theme.palette.warning.main
                  : theme.palette.divider,
                color: store.isInsertingPoint
                  ? theme.palette.warning.contrastText
                  : theme.palette.text.primary,
                backgroundColor: store.isInsertingPoint
                  ? theme.palette.warning.main
                  : 'transparent',
                fontWeight: store.isInsertingPoint ? 'bold' : 'normal',
                '&:hover': {
                  borderColor: store.isInsertingPoint
                    ? theme.palette.warning.dark
                    : theme.palette.primary.main,
                  backgroundColor: store.isInsertingPoint
                    ? theme.palette.warning.dark
                    : alpha(theme.palette.action.hover, 0.1),
                },
              }}
            >
              {menuOptions.editStyle}
            </Button>

            {/* Selection operations*/}
            {canCopyPoints && (
              <>
                {/* Show selection status when points are selected */}
                {hasSelectedAll && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.success.main,
                      textAlign: 'center',
                      fontStyle: 'italic',
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      mt: 0.5,
                      mb: 1,
                    }}
                  >
                    ✓ All{' '}
                    {currentTakeoffAssembly?.pointsList?.filter(point => point.isactive).length ||
                      0}{' '}
                    points selected
                  </Typography>
                )}

                {/* Clear Selection button - show when points are selected */}
                {hasSelectedAll && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => {
                      setSelectedPoints([]);
                      setHasSelectedAll(false);
                      // Clear paste preview when clearing selection
                      document.dispatchEvent(new CustomEvent('app:clearpastepreview'));
                      if ((window as any).__pastePreviewActive) {
                        (window as any).__pastePreviewActive = false;
                      }
                      if ((window as any).__pastePositionFrozen) {
                        (window as any).__pastePositionFrozen = false;
                      }
                    }}
                    sx={{
                      borderColor: theme.palette.warning.main,
                      color: theme.palette.warning.main,
                      '&:hover': {
                        borderColor: theme.palette.warning.dark,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      },
                    }}
                  >
                    Clear Selection
                  </Button>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleCopyExtend}
                  sx={{
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  Copy Extend{' '}
                  {includeAllPoints && !hasSelectedPoints
                    ? `(All ${currentTakeoffAssembly?.pointsList?.filter(point => point.isactive).length || 0})`
                    : `(${selectedPoints ? selectedPoints.length : 0})`}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleCopyAsNew}
                  sx={{
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  Copy as New
                </Button>

                {/* Only show delete selection button if points are actually selected */}
                {hasSelectedPoints && (
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    fullWidth
                    onClick={handleDeleteSelection}
                  >
                    Delete Selection
                  </Button>
                )}
              </>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Point options section */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAllPoints}
                  onChange={handleIncludeAllPointsChange}
                  size="small"
                />
              }
              label={<Typography variant="body2">{menuOptions.includeAllPoints}</Typography>}
            />

            {/* Allow Trailing Line */}
            {currentTakeoffAssembly?.assemblymeasurementtype === 'length' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localAllowTrailingLine}
                    onChange={e => handleToggleAllowTrailingLine(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {menuOptions.allowTrailingLine}
                    {hasSelectedPoints && selectedPoints.length > 1
                      ? ` (${selectedPoints.length} points)`
                      : includeAllPoints
                        ? ` (All ${currentTakeoffAssembly?.pointsList?.filter(point => point.isactive).length || 0})`
                        : ''}
                  </Typography>
                }
              />
            )}

            {/* Include in Count */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={localIsIncludedInCount}
                  onChange={e => handleToggleIncludeInCount(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {menuOptions.toggleIncludeInCount}
                  {hasSelectedPoints && selectedPoints.length > 1
                    ? ` (${selectedPoints.length} points)`
                    : includeAllPoints
                      ? ` (All ${currentTakeoffAssembly?.pointsList?.filter(point => point.isactive).length || 0})`
                      : ''}
                </Typography>
              }
            />

            {/* Delete Point */}
            {!hasSelectedPoints && (
              <Button
                variant="contained"
                color="error"
                size="small"
                fullWidth
                onClick={() => handleDeletePoint(assemblyIndex!, pointIndex!)}
              >
                {menuOptions.deletePoint}
              </Button>
            )}

            {/* Vertical length input with apply button */}
            {currentTakeoffAssembly?.assemblymeasurementtype === 'length' &&
              (isPointEditing || hasSelectedPoints) && (
                <>
                  <TextField
                    label={
                      hasSelectedPoints && selectedPoints.length > 1
                        ? `Add length to ${selectedPoints.length} points (ft)`
                        : 'Add length (ft)'
                    }
                    type="number"
                    value={verticalLength}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">ft</InputAdornment>,
                    }}
                    onChange={handleVerticalLengthChange}
                    onKeyDown={handleKeyDown}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    helperText="Press Enter to apply"
                    variant="outlined"
                    sx={{
                      '& .MuiFormHelperText-root': {
                        marginLeft: 0,
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  {hasSelectedPoints && selectedPoints.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => handleAddVertical(-1)}
                      sx={{
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        },
                      }}
                    >
                      Apply Length to Selection
                    </Button>
                  )}
                </>
              )}
          </Stack>
        </Box>
      </Paper>
    </Draggable>
  );
};
